import {
  buildEqualCreditInstallments,
  formatInstallmentTotalMismatchMessage,
  getInstallmentPlanBalance,
  sumInstallmentAmounts,
  type CreditInstallmentPlanRow,
} from "@/shared/lib/credit-invoice-installments";
import { useClassTemplatesQuery } from "@/modules/classes/presentation/api/queries";
import {
  SellPartnerProjectObjectSchema,
  type SellPartnerProjectDTO,
} from "@/modules/packages/domain/contracts";
import {
  OFF_SITE_DISTANCE_SELECT_OPTIONS,
  type OffSiteDistance,
} from "@/modules/private-lessons/domain/lib/off-site-distance";
import { config } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  formatDate,
  normalizeSellPackageStartDateValue,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import { fetchLookupForInfinite, hydrateLookupItems } from "@/shared/lib/utils";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import type { SellPackageDiscountType } from "@/shared/ui/SellPackageDiscountTypeRadio";
import { SellPackageDiscountTypeRadio } from "@/shared/ui/SellPackageDiscountTypeRadio";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Checkbox,
  FormDatePicker,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  FormTextarea,
  Label,
  RHF,
  cn,
  zodResolver
} from "@devhop/ui";
import { addDays, format, parseISO } from "date-fns";
import {
  Building2Icon,
  CalendarIcon,
  InfoIcon,
  Receipt,
  ScrollText,
} from "lucide-react";
import { useEffect, useMemo, type PropsWithChildren } from "react";
import { z } from "zod";

type InstructorItem = { id: string; name: string };

const DAYS_OF_WEEK: { value: number; label: string }[] = [
  { value: 1, label: "ຈັນ" },
  { value: 2, label: "ອັງຄານ" },
  { value: 3, label: "ພຸດ" },
  { value: 4, label: "ພະທັດ" },
  { value: 5, label: "ສຸກ" },
  { value: 6, label: "ເສົາ" },
  { value: 7, label: "ອາທິດ" },
];

const slotDurZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const PartnerDayRowSchema = z.object({
  dayOfWeek: z.number(),
  label: z.string(),
  enabled: z.boolean(),
  startTime: z.string(),
  slotDurationMinutes: slotDurZ,
  roomId: z.string(),
});

type PartnerScheduleRow = z.infer<typeof PartnerDayRowSchema>;

function partnerScheduleRowsDefault(): PartnerScheduleRow[] {
  return DAYS_OF_WEEK.map((d) => ({
    dayOfWeek: d.value,
    label: d.label,
    enabled: false,
    startTime: "09:00",
    slotDurationMinutes: 60 as PrivateLessonSlotDurationMinutes,
    roomId: "",
  }));
}

const EMPTY_INSTALLMENT_ROWS: CreditInstallmentPlanRow[] = [];

const PartnerInstallmentRowSchema = z.object({
  installmentNo: z.number(),
  label: z.string(),
  dueDate: z.string(),
  amount: z.coerce.number().int().min(1, "ຍອດງວດຕ້ອງຫຼາຍກວ່າ 0"),
});

const PartnerPerClassBlockSchema = z.object({
  templateId: z.string().min(1),
  instructorIds: z.array(z.string().min(1)).min(1, "ຕ້ອງເລືອກຢ່າງນ້ອຍ 1 ຜູ້ສອນ"),
  capacity: z.coerce.number().int().positive(),
  notes: z.string().max(2000).nullable().optional(),
  scheduleDayRows: z.array(PartnerDayRowSchema),
});

type PartnerScheduleBlockForm = z.infer<typeof PartnerPerClassBlockSchema>;

function syncPartnerBlocksFromTemplates(
  templateIds: string[],
  prev: PartnerScheduleBlockForm[],
): PartnerScheduleBlockForm[] {
  const defaults = partnerScheduleRowsDefault();
  const byTpl = new Map(prev.map((b) => [b.templateId, b]));
  return templateIds.map((templateId) => {
    const ex = byTpl.get(templateId);
    if (ex)
      return {
        ...ex,
        templateId,
        scheduleDayRows:
          ex.scheduleDayRows?.length === 7 ? ex.scheduleDayRows : [...defaults],
      };
    return {
      templateId,
      instructorIds: [],
      capacity: 20,
      notes: "",
      scheduleDayRows: defaults.map((r) => ({ ...r })),
    };
  });
}

export const SellPartnerOffSiteFormSchema = SellPartnerProjectObjectSchema.omit(
  {
    startDate: true,
    expireDate: true,
    discountType: true,
    discountValue: true,
    schedules: true,
  },
)
  .extend({
    startDate: z.preprocess(normalizeSellPackageStartDateValue, z.date()),
    validDays: z.coerce.number().int().min(1).max(3650),
    pricingStatus: z.enum(["draft", "quoted", "locked"]).default("locked"),
    provisionalTotal: z.coerce.number().int().min(0).optional(),
    discountType: z.enum(["percentage", "fixed", "none"]).optional(),
    discountValue: z.number().optional(),
    partnerSchedules: z.array(PartnerPerClassBlockSchema),
    installmentRows: z.array(PartnerInstallmentRowSchema).optional(),
  })
  .refine(
    (data) => {
      const ids = data.classTemplateIds;
      const u = [...new Set(ids)];
      return u.length === ids.length;
    },
    {
      message: "ເລືອກແມ່ແບບ ບໍ່ຊ້ຳ",
      path: ["classTemplateIds"],
    },
  )
  .refine(
    (data) => {
      if (!data.discountType || data.discountType === "none") return true;
      if (data.discountValue === undefined) return false;
      if (data.discountType === "percentage") {
        return data.discountValue >= 0 && data.discountValue <= 100;
      }
      if (data.discountType === "fixed") {
        return data.discountValue > 0;
      }
      return true;
    },
    { message: "ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ" },
  )
  .refine(
    (data) => {
      const tpl = data.classTemplateIds;
      const uniq = [...new Set(tpl)];
      if (uniq.length !== tpl.length) return false;
      if (data.partnerSchedules.length !== tpl.length) return false;
      for (let i = 0; i < tpl.length; i++) {
        if (data.partnerSchedules[i]?.templateId !== tpl[i]) return false;
      }
      return true;
    },
    {
      message: "ຕາຕະລາງ ຕ້ອງກົງກັບ ລຳດັບ ແມ່ແບບໃນຂັ້ນທີ 1 (ບໍ່ ຊ້ຳ ບໍ່ ຂາດ)",
      path: ["partnerSchedules"],
    },
  )
  .superRefine((data, ctx) => {
    data.partnerSchedules.forEach((block, bi) => {
      const rows = block.scheduleDayRows;
      const hasDay = rows.some((r) => r.enabled);
      if (!hasDay) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ເປີດໃຊ້ ຢ່າງນ້ອຍ 1 ວັນ ຕໍ່ແມ່ແບບແຕ່ລະອັນ",
          path: ["partnerSchedules", bi, "scheduleDayRows"],
        });
      }
      for (const r of rows) {
        if (!r.enabled) continue;
        const st = r.startTime?.trim();
        if (!st || r.slotDurationMinutes == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "ເວລາເລີ່ມ ແລະ ຄວາມຍາວ (ນາທີ) ຕ້ອງກຳນົດຄົບ ທຸກວັນທີ່ເປີດໃຊ້",
            path: ["partnerSchedules", bi, "scheduleDayRows"],
          });
          break;
        }
      }
    });
  })
  .superRefine((data, ctx) => {
    if (data.pricingStatus !== "locked") return;
    if (data.settlementMode !== "credit") return;

    let finalPrice = 0;
    const originalPrice =
      typeof data.price === "number" &&
      Number.isFinite(data.price) &&
      data.price > 0
        ? data.price
        : 0;
    if (originalPrice <= 0) return;

    finalPrice = originalPrice;
    const dt = data.discountType;
    if (
      dt &&
      dt !== "none" &&
      data.discountValue !== undefined &&
      Number.isFinite(data.discountValue)
    ) {
      let discountAmount = 0;
      if (dt === "percentage") {
        discountAmount = Math.round((originalPrice * data.discountValue) / 100);
      } else {
        discountAmount = Math.round(data.discountValue);
      }
      discountAmount = Math.min(discountAmount, originalPrice);
      finalPrice = originalPrice - discountAmount;
      if (finalPrice < 0) finalPrice = 0;
    }

    const rows = data.installmentRows ?? [];
    if (rows.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງກຳນົດແຜນງວດຊຳລະ (ຢ່າງນ້ອຍ 2 ງວດ)",
        path: ["installmentRows"],
      });
      return;
    }

    if (!getInstallmentPlanBalance(rows, finalPrice).isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatInstallmentTotalMismatchMessage(
          getInstallmentPlanBalance(rows, finalPrice).sum,
          finalPrice,
        ),
        path: ["installmentRows"],
      });
    }
  });

export type SellPartnerOffSiteFormValues = z.infer<
  typeof SellPartnerOffSiteFormSchema
>;

type SellPartnerOffSiteFormProps = {
  onSubmit: (values: SellPartnerProjectDTO) => void | Promise<void>;
  submitting: boolean;
};

function StepCard({
  step,
  title,
  subtitle,
  required,
  children,
}: PropsWithChildren<{
  step: number;
  title: string;
  subtitle?: string;
  required?: boolean;
}>) {
  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
          {step}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg">{title}</h3>
            {required ? (
              <span className="rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
                ຈຳເປັນ
              </span>
            ) : null}
          </div>
          {subtitle ? (
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

export function SellPartnerOffSiteForm({
  onSubmit,
  submitting,
}: SellPartnerOffSiteFormProps) {
  const methods = RHF.useForm<SellPartnerOffSiteFormValues>({
    // @ts-expect-error - zod preprocessor + zodResolver vs RHF Resolver typing
    resolver: zodResolver(SellPartnerOffSiteFormSchema),
    defaultValues: {
      classTemplateIds: [],
      partnerSchedules: [],
      pricingStatus: "locked",
      provisionalTotal: undefined,
      price: 0,
      partnerLabel: "",
      offSiteDistance: "near" as OffSiteDistance,
      notes: "",
      startDate: new Date(),
      validDays: 30,
      discountType: "none",
      discountValue: undefined,
      settlementMode: "receipt",
      amountPaidNow: 0,
      installmentCount: 2,
      installmentRows: [] as CreditInstallmentPlanRow[],
    },
  });

  const watchedStartDate = methods.watch("startDate");
  const watchedValidDays = methods.watch("validDays");
  const discountTypeWatch = methods.watch("discountType");
  const discountValueWatch = methods.watch("discountValue");
  const pricingStatus = methods.watch("pricingStatus");
  const settlementMode = methods.watch("settlementMode");
  const watchedPrice = methods.watch("price");
  const installmentCountWatch = methods.watch("installmentCount");
  const installmentRowsWatch =
    RHF.useWatch({
      control: methods.control,
      name: "installmentRows",
      defaultValue: EMPTY_INSTALLMENT_ROWS,
    }) ?? EMPTY_INSTALLMENT_ROWS;

  const expirePreview = useMemo(() => {
    const d = normalizeSellPackageStartDateValue(watchedStartDate);
    const days = watchedValidDays;
    if (
      watchedStartDate == null ||
      typeof days !== "number" ||
      Number.isNaN(days) ||
      days < 1
    ) {
      return null;
    }
    try {
      return format(addDays(d, days), "yyyy-MM-dd");
    } catch {
      return null;
    }
  }, [watchedStartDate, watchedValidDays]);

  const priceCalculation = useMemo(() => {
    const originalPrice =
      typeof watchedPrice === "number" &&
        Number.isFinite(watchedPrice) &&
        watchedPrice > 0
        ? watchedPrice
        : 0;
    if (originalPrice <= 0) {
      return { originalPrice: 0, discountAmount: 0, finalPrice: 0 };
    }
    const dt = discountTypeWatch;
    let discountAmount = 0;
    let finalPrice = originalPrice;
    if (
      dt &&
      dt !== "none" &&
      discountValueWatch !== undefined &&
      Number.isFinite(discountValueWatch)
    ) {
      if (dt === "percentage") {
        discountAmount = Math.round((originalPrice * discountValueWatch) / 100);
      } else {
        discountAmount = Math.round(discountValueWatch);
      }
      discountAmount = Math.min(discountAmount, originalPrice);
      finalPrice = originalPrice - discountAmount;
      if (finalPrice < 0) {
        finalPrice = 0;
        discountAmount = originalPrice;
      }
    }
    return { originalPrice, discountAmount, finalPrice };
  }, [watchedPrice, discountTypeWatch, discountValueWatch]);

  const installmentPlanBalance = useMemo(
    () =>
      getInstallmentPlanBalance(
        installmentRowsWatch,
        priceCalculation.finalPrice,
      ),
    [installmentRowsWatch, priceCalculation.finalPrice],
  );
  const installmentPlanInvalid =
    pricingStatus === "locked" &&
    settlementMode === "credit" &&
    priceCalculation.finalPrice > 0 &&
    !installmentPlanBalance.isValid;

  const rebuildInstallmentRows = () => {
    const fp = priceCalculation.finalPrice;
    const count =
      typeof installmentCountWatch === "number" &&
      Number.isFinite(installmentCountWatch)
        ? installmentCountWatch
        : 2;
    if (fp <= 0 || count < 2) {
      methods.setValue("installmentRows", []);
      return;
    }
    const start = normalizeSellPackageStartDateValue(watchedStartDate);
    const startStr = format(start, "yyyy-MM-dd");
    try {
      methods.setValue(
        "installmentRows",
        buildEqualCreditInstallments(fp, count, startStr),
      );
    } catch {
      methods.setValue("installmentRows", []);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: rebuild when count/price/start changes only
  useEffect(() => {
    if (settlementMode !== "credit") return;
    rebuildInstallmentRows();
  }, [
    settlementMode,
    priceCalculation.finalPrice,
    installmentCountWatch,
    watchedStartDate,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: methods stable; guard prevents redundant setError
  useEffect(() => {
    if (settlementMode !== "credit" || priceCalculation.finalPrice <= 0) {
      if (methods.formState.errors.installmentRows) {
        methods.clearErrors("installmentRows");
      }
      return;
    }
    if (installmentRowsWatch.length < 2) return;

    const mismatchMessage = formatInstallmentTotalMismatchMessage(
      installmentPlanBalance.sum,
      installmentPlanBalance.total,
    );
    if (!installmentPlanBalance.isValid) {
      if (methods.formState.errors.installmentRows?.message !== mismatchMessage) {
        methods.setError("installmentRows", {
          type: "manual",
          message: mismatchMessage,
        });
      }
      return;
    }
    if (methods.formState.errors.installmentRows) {
      methods.clearErrors("installmentRows");
    }
  }, [
    settlementMode,
    priceCalculation.finalPrice,
    installmentPlanBalance.sum,
    installmentPlanBalance.total,
    installmentPlanBalance.isValid,
    installmentRowsWatch.length,
  ]);

  const { data: classTemplatesPages, isLoading: classTemplatesLoading } =
    useClassTemplatesQuery({
      limit: 200,
      offset: 0,
      filters: [{ field: "status", op: "eq", value: "active" }],
    });

  const classTemplates = classTemplatesPages?.data ?? [];

  const selectedClassTemplateIds = methods.watch("classTemplateIds") ?? [];
  const partnerSchedulesTplKey = selectedClassTemplateIds.join("|");

  const partnerSchedulesWatch =
    methods.watch("partnerSchedules") ?? ([] as PartnerScheduleBlockForm[]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `partnerSchedulesTplKey` tracks classTemplateIds; `methods` ref is stable
  useEffect(() => {
    const ids = [...(methods.getValues("classTemplateIds") ?? [])];
    const prev = methods.getValues("partnerSchedules") ?? [];
    const next = syncPartnerBlocksFromTemplates(ids, prev);
    methods.setValue("partnerSchedules", next);
  }, [methods, partnerSchedulesTplKey]);

  const rowEndScheduleDisplay = (row: PartnerScheduleRow): string => {
    if (!row.startTime?.trim()) return "—";
    try {
      return addMinutesToTimeHhMm(
        row.startTime.trim(),
        row.slotDurationMinutes,
      );
    } catch {
      return "—";
    }
  };

  const toggleScheduleDay = (blockIndex: number, dayIndex: number) => {
    const blocks = [
      ...(methods.getValues("partnerSchedules") ?? []),
    ] as PartnerScheduleBlockForm[];
    const block = blocks[blockIndex];
    if (!block) return;
    const rows = [...block.scheduleDayRows];
    const row = rows[dayIndex];
    if (!row) return;
    rows[dayIndex] = {
      ...row,
      enabled: !row.enabled,
      startTime: row.startTime || "09:00",
      slotDurationMinutes: PRIVATE_LESSON_SLOT_DURATION_SET.has(
        row.slotDurationMinutes,
      )
        ? row.slotDurationMinutes
        : 60,
      roomId: "",
    };
    blocks[blockIndex] = { ...block, scheduleDayRows: rows };
    methods.setValue("partnerSchedules", blocks);
  };

  const updateScheduleDayRow = (
    blockIndex: number,
    dayIndex: number,
    field: keyof PartnerScheduleRow,
    value: unknown,
  ) => {
    const blocks = [
      ...(methods.getValues("partnerSchedules") ?? []),
    ] as PartnerScheduleBlockForm[];
    const block = blocks[blockIndex];
    if (!block) return;
    const rows = [...block.scheduleDayRows];
    const row = rows[dayIndex];
    if (!row) return;
    rows[dayIndex] = { ...row, [field]: value };
    blocks[blockIndex] = { ...block, scheduleDayRows: rows };
    methods.setValue("partnerSchedules", blocks);
  };

  const toHHmmss = (raw: string) => {
    const s = raw.trim();
    return s.length === 5 ? `${s}:00` : s;
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    const base = normalizeSellPackageStartDateValue(data.startDate);
    const startDateStr = format(base, "yyyy-MM-dd");
    const expireDateStr = format(addDays(base, data.validDays), "yyyy-MM-dd");

    const dt =
      data.discountType && data.discountType !== "none"
        ? data.discountType
        : undefined;
    const dv =
      dt && data.discountValue !== undefined ? data.discountValue : undefined;

    const fp = priceCalculation.finalPrice;
    const status = data.pricingStatus ?? "locked";
    if (status === "locked" && data.settlementMode === "credit" && fp > 0) {
      const apn = data.amountPaidNow ?? 0;
      if (apn > fp) {
        methods.setError("amountPaidNow", {
          type: "manual",
          message: "ບໍ່ເກີນລາຄາສຸດທ້າຍ",
        });
        return;
      }
      const rows = data.installmentRows ?? [];
      const balance = getInstallmentPlanBalance(rows, fp);
      if (!balance.isValid) {
        methods.setError("installmentRows", {
          type: "manual",
          message: formatInstallmentTotalMismatchMessage(
            balance.sum,
            balance.total,
          ),
        });
        return;
      }
    }

    const schedules = data.partnerSchedules.map((block) => {
      const daySchedules = block.scheduleDayRows
        .filter((r: PartnerScheduleRow) => r.enabled)
        .map((r: PartnerScheduleRow) => {
          const start = (r.startTime || "09:00").trim();
          const dur = r.slotDurationMinutes;
          return {
            dayOfWeek: r.dayOfWeek,
            startTime: toHHmmss(start),
            endTime: toHHmmss(addMinutesToTimeHhMm(start, dur)),
          };
        });

      return {
        templateId: block.templateId,
        instructorIds: block.instructorIds,
        capacity: block.capacity,
        notes:
          typeof block.notes === "string" && block.notes.trim()
            ? block.notes.trim()
            : null,
        daySchedules,
      };
    });

    await onSubmit({
      classTemplateIds: data.classTemplateIds,
      pricingStatus: status,
      provisionalTotal:
        status === "draft" ? (data.provisionalTotal ?? null) : undefined,
      price: status === "draft" ? undefined : data.price,
      partnerLabel: data.partnerLabel?.trim()
        ? data.partnerLabel.trim()
        : undefined,
      offSiteDistance: data.offSiteDistance ?? "near",
      notes: data.notes?.trim() ? data.notes.trim() : undefined,
      startDate: startDateStr,
      expireDate: expireDateStr,
      discountType: status === "draft" ? undefined : dt,
      discountValue: status === "draft" ? undefined : dv,
      settlementMode: status === "locked" ? data.settlementMode : undefined,
      amountPaidNow:
        status === "locked" && data.settlementMode === "credit"
          ? Math.min(Math.max(0, data.amountPaidNow ?? 0), fp)
          : 0,
      installmentCount:
        status === "locked" && data.settlementMode === "credit"
          ? (data.installmentRows?.length ?? data.installmentCount ?? 2)
          : undefined,
      installments:
        status === "locked" &&
        data.settlementMode === "credit" &&
        data.installmentRows?.length
          ? data.installmentRows.map((row) => ({
              installmentNo: row.installmentNo,
              label: row.label,
              dueDate: row.dueDate,
              amount: row.amount,
            }))
          : undefined,
      schedules,
    });
  });

  return (
    // @ts-expect-error - FormRoot onSubmit typed as values callback elsewhere in workspace
    <FormRoot methods={methods} onSubmit={handleSubmit}>
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <StepCard
            step={1}
            title="ແມ່ແບບຄລາດສ໌ທີ່ອອກໄປຊຽນ"
            subtitle="ເລືອກຢ່າງນ້ອຍ 1 ຄລາດສ໌ຈາກລະບົບ — ບໍ່ເລືອກແພັກເກດນັກຮຽນ"
            required
          >
            <div className="space-y-3 rounded-lg border p-4">
              <div>
                <span className="block font-medium text-sm">
                  ເລືອກແມ່ແບບຄລາດສ໌
                  <span className="text-destructive"> *</span>
                </span>
                <p className="text-muted-foreground text-xs">
                  ສາມາດເລືອກຫຼາຍແມ່ແບບໄດ້ ຄ້າຍກັນກັບສ່ວນເລືອກຄລາດສ໌ເມື່ອຂາຍແພັກເກດ
                </p>
              </div>
              {classTemplatesLoading ? (
                <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
              ) : classTemplates.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  ບໍ່ມີແມ່ແບບທີ່ສະຖານະ active
                </p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {classTemplates.map((tpl) => {
                    const isChecked = selectedClassTemplateIds.includes(tpl.id);
                    return (
                      <Label
                        key={tpl.id}
                        htmlFor={`partner-template-${tpl.id}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${isChecked
                          ? "border-red-600 bg-red-50 dark:bg-red-950/20"
                          : "hover:bg-muted/50"
                          }`}
                      >
                        <Checkbox
                          id={`partner-template-${tpl.id}`}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const current =
                              methods.getValues("classTemplateIds") ?? [];
                            if (checked) {
                              if (!current.includes(tpl.id)) {
                                methods.setValue("classTemplateIds", [
                                  ...current,
                                  tpl.id,
                                ]);
                              }
                            } else {
                              methods.setValue(
                                "classTemplateIds",
                                current.filter((id) => id !== tpl.id),
                              );
                            }
                            void methods.trigger("classTemplateIds");
                            void methods.trigger("partnerSchedules");
                          }}
                        />
                        <span>
                          {tpl.classTypeName} - {tpl.levelName}
                        </span>
                      </Label>
                    );
                  })}
                </div>
              )}
              {methods.formState.errors.classTemplateIds ? (
                <p className="text-destructive text-sm">
                  {typeof methods.formState.errors.classTemplateIds.message ===
                    "string"
                    ? methods.formState.errors.classTemplateIds.message
                    : "ເລືອກຢ່າງນ້ອຍ 1 ແມ່ແບບ"}
                </p>
              ) : null}
            </div>
          </StepCard>

          <StepCard
            step={2}
            title="ໄລຍະເວລາການໃຊ້ງານ"
            subtitle="ກຳນົດເວລາມີຜົນເກັບງານຂາຍ ຄ້າຍກັນກັບປະເພດຄລາສ໌ ແລະ ຮຽນສ່ວນຕົວ"
            required
          >
            <p className="mb-4 text-muted-foreground text-sm">
              ວັນທີ່ຫມົດອາຍຸ = ວັນເລີ່ມ + ຈຳນວນວັນທີ່ຕັ້ງຕອນຂາຍດ້ານລຸ່ມນີ້.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <FormDatePicker
                  name="startDate"
                  label="ວັນທີເລີ່ມຕົ້ນ"
                  requiredMark
                  placeholder="ເລືອກວັນທີເລີ່ມຕົ້ນ..."
                />
                <p className="text-muted-foreground text-xs">
                  ມື້ນີ້ເປັນຄ່າເລີ່ມຕົ້ນຖ້າບໍ່ປັບ
                </p>
              </div>

              <div className="space-y-2">
                <FormNumber
                  name="validDays"
                  label="ຈຳນວນວັນທີ່ນັບນັບຖືກຈາກວັນເລີ່ມ"
                  placeholder="ເຊັ່ນ 30"
                  min={1}
                  max={3650}
                  requiredMark
                />
                <p className="text-muted-foreground text-xs">
                  ກຳນົດຄົບກັບຂາຍຮຽນສ່ວນຕົວ ຫຼື ຈົນກວ່າຈະຫມົດອາຍຸອັດຕະໂນມັດ
                </p>
              </div>

              {expirePreview ? (
                <div className="space-y-2">
                  <span className="block font-medium text-sm">ວັນທີ່ຫມົດອາຍຸ</span>
                  <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                    <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 font-medium text-sm">
                      {format(parseISO(expirePreview), "MMM dd, yyyy")}
                    </span>
                    <span className="rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs dark:bg-red-950/60 dark:text-red-200">
                      +{watchedValidDays ?? 0} ວັນ
                    </span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    ຄ່າທີ່ຄິດໄລ່ອັດຕະໂນມັດ ({formatDate(expirePreview)})
                  </p>
                </div>
              ) : null}
            </div>
          </StepCard>

          <StepCard
            step={3}
            title="ຄຸນສົມບັດໂຄງການ"
            subtitle="ຝາກຊື່ຜູ້ຈ້າງ / ຫ້ອງ ຫຼື ໝາຍເຫດສັ້ນ"
          >
            <FormInput
              name="partnerLabel"
              label="ຊື່ partner / ສະຖານທີ່"
              placeholder="ເຊັ່ນ ຊື່ໂຮງຮຽນ ຫຼື ຊື່ໂຄງການ"
            />
            <div className="space-y-1.5">
              <Label className="text-sm">
                ໄລຍະນອກສະຖານທີ່
                <span className="text-destructive"> *</span>
              </Label>
              <RHF.Controller
                name="offSiteDistance"
                control={methods.control}
                render={({ field }) => (
                  <SimpleSelect
                    value={field.value ?? "near"}
                    onValueChange={(v) =>
                      field.onChange(v as OffSiteDistance)
                    }
                    options={[...OFF_SITE_DISTANCE_SELECT_OPTIONS]}
                    className="h-9 w-full max-w-xs"
                  />
                )}
              />
              <p className="text-muted-foreground text-xs">
                ລະບົບຕັ້ງ session ເປັນນອກສະຖານທີ່ໂດຍອັດຕະໂນມັດ — ເລືອກໃກ້ ຫຼື ໄກ
              </p>
            </div>
            <FormTextarea
              name="notes"
              label="ໝາຍເຫດ"
              placeholder="ບໍ່ບັງຄັບ"
              rows={3}
            />
          </StepCard>

          <StepCard
            step={4}
            title="ຜູ້ສອນ ຕາຕະລາງ ຕໍ່ແມ່ແບບຄລາດສ໌"
            subtitle="ແຕ່ລະແມ່ແບບທີ່ເລືອກມີຕາຕະລາງ ເວລາ ແລະ ຄວາມຍາວ (ນາທີ) ແຍກກັນ — ລະບົບຕັ້ງ ຊົ່ວໂມງຮຽນ ເປັນນອກສະຖານທີ່ໂດຍອັດຕະໂນມັດ"
            required
          >
            <p className="mb-4 text-muted-foreground text-sm">
              ແມ່ແບບຜູກຄບກັບ ຂັ້ນທີ 1 ຕາມລຳດັບ ທີ່ເລືອກ. ບັນທຶກການຂາຍຈະນຳໃຊ້ ຊື່ partner
              (ປ້ອນຂັ້ນທີ 3) ເປັນສະຖານທີ່ session.
            </p>
            <p className="mb-4 flex items-center gap-1 text-red-600 text-xs">
              <InfoIcon className="size-3.5 shrink-0" />
              ກົດດຽວກັນກັບ «ສ້າງຫຼາຍຊົ່ວໂມງຮຽນ»: ຖ້າກົດ ຊ້ຳເວລາ ຫຼື ຄູ ລະບົບຈະບໍ່ບັນທຶກການຂາຍ
            </p>
            {selectedClassTemplateIds.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ເລືອກແມ່ແບບຄລາດສ໌ຂັ້ນທີ 1 ກ່ອນຈາກນັ້ນຄ່ອຍກຳນົດຜູ້ສອນ ກັບຕາຕະລາງດ້ານລຸ່ມນີ້.
              </p>
            ) : (
              <div className="space-y-8">
                {partnerSchedulesWatch.map((block, bi) => {
                  const meta = classTemplates.find(
                    (t) => t.id === block.templateId,
                  );
                  const title =
                    meta != null
                      ? `${meta.classTypeName} - ${meta.levelName}`
                      : block.templateId;

                  const blockRowErr =
                    methods.formState.errors.partnerSchedules?.[bi];
                  const schedErrRaw = blockRowErr?.scheduleDayRows;
                  const schedErrMsg =
                    schedErrRaw &&
                      typeof schedErrRaw === "object" &&
                      "message" in schedErrRaw &&
                      typeof (schedErrRaw as { message?: unknown }).message ===
                      "string"
                      ? (schedErrRaw as { message: string }).message
                      : typeof schedErrRaw === "object" &&
                        schedErrRaw &&
                        "_errors" in schedErrRaw
                        ? (schedErrRaw as { _errors?: string[] })._errors?.[0]
                        : undefined;

                  return (
                    <div
                      key={block.templateId}
                      className="space-y-4 rounded-xl border bg-card p-4"
                    >
                      <div className="border-b pb-3">
                        <h4 className="font-semibold text-base">{title}</h4>
                        <p className="text-muted-foreground text-xs">
                          ID ແມ່ແບບ: {block.templateId}
                        </p>
                      </div>

                      <FormInfiniteMultiCombobox<InstructorItem>
                        name={`partnerSchedules.${bi}.instructorIds`}
                        label="ຜູ້ສອນສຳລັບຄລາດສ໌ນີ້"
                        hint="ເລືອກຢ່າງນ້ອຍ 1 ທ່ານ"
                        queryKey={[
                          "master",
                          "instructors",
                          "lookup",
                          `sell-partner-${bi}`,
                        ]}
                        queryFn={({ search, pageParam }) =>
                          fetchLookupForInfinite<InstructorItem>(
                            `${config.apiUrl}/master/instructors/lookup`,
                            { search, pageParam },
                          )
                        }
                        preloadQueryFn={(ids) =>
                          hydrateLookupItems<InstructorItem>(
                            `${config.apiUrl}/master/instructors/lookup`,
                            ids,
                          )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        placeholder="ເລືອກຄູ..."
                      />

                      <FormNumber
                        name={`partnerSchedules.${bi}.capacity`}
                        label="ຄວາມຈຸຊົ່ວໂມງຮຽນ (ນັກຮຽນ)"
                        min={1}
                        requiredMark
                      />
                      <FormTextarea
                        name={`partnerSchedules.${bi}.notes`}
                        label="ໝາຍເຫດ ກຳນົດກັບ ຊົ່ວໂມງຮຽນ ຂອງຄລາດສ໌ນີ້"
                        placeholder="ບໍ່ບັງຄັບ"
                        rows={2}
                      />

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="pr-4 pb-2 text-left font-medium">
                                ວັນ
                              </th>
                              <th className="pr-4 pb-2 text-left font-medium">
                                ເປີດໃຊ້
                              </th>
                              <th className="pr-4 pb-2 text-left font-medium">
                                ເວລາເລີ່ມ
                              </th>
                              <th className="pr-4 pb-2 text-left font-medium">
                                ຄວາມຍາວ (ນາທີ)
                              </th>
                              <th className="pb-2 text-left font-medium">
                                ສິ້ນສຸດ (ຄຳນວນ)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(
                              block.scheduleDayRows ??
                              partnerScheduleRowsDefault()
                            ).map((row, dayIdx) => (
                              <tr
                                key={row.dayOfWeek}
                                className={`border-b ${row.enabled ? "bg-muted/30" : ""}`}
                              >
                                <td className="py-2 pr-4 font-medium">
                                  {row.label}
                                </td>
                                <td className="py-2 pr-4">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleScheduleDay(bi, dayIdx)
                                    }
                                    className={`rounded-md border px-3 py-1.5 font-medium text-sm transition-colors ${row.enabled
                                      ? "border-red-600 bg-red-600 text-white"
                                      : "border-border bg-background text-muted-foreground hover:bg-muted"
                                      }`}
                                  >
                                    {row.enabled ? "ເປີດຢູ່" : "ປິດ"}
                                  </button>
                                </td>
                                <td className="py-2 pr-4">
                                  <QuarterHourMinuteSelect
                                    value={row.startTime || "09:00"}
                                    onChange={(next) =>
                                      updateScheduleDayRow(
                                        bi,
                                        dayIdx,
                                        "startTime",
                                        next,
                                      )
                                    }
                                    disabled={!row.enabled}
                                    className="max-w-[200px]"
                                  />
                                </td>
                                <td className="py-2 pr-4 align-top">
                                  <SimpleSelect
                                    value={String(row.slotDurationMinutes)}
                                    onValueChange={(val) => {
                                      const m = Number(val);
                                      if (
                                        !PRIVATE_LESSON_SLOT_DURATION_SET.has(m)
                                      )
                                        return;
                                      updateScheduleDayRow(
                                        bi,
                                        dayIdx,
                                        "slotDurationMinutes",
                                        m as PrivateLessonSlotDurationMinutes,
                                      );
                                    }}
                                    options={
                                      PRIVATE_LESSON_DURATION_SELECT_OPTIONS
                                    }
                                    className="h-9 min-w-[120px] max-w-[220px]"
                                    disabled={!row.enabled}
                                    placeholder="ເລືອກຄວາມຍາວ"
                                  />
                                </td>
                                <td className="py-2 align-top">
                                  <div className="flex min-h-9 min-w-[72px] max-w-[140px] items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                                    {row.enabled
                                      ? rowEndScheduleDisplay(row)
                                      : "—"}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {schedErrMsg ? (
                        <p className="text-destructive text-xs">
                          {schedErrMsg}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
            {typeof methods.formState.errors.partnerSchedules?.message ===
              "string" ? (
              <p className="mt-4 text-destructive text-xs">
                {methods.formState.errors.partnerSchedules.message}
              </p>
            ) : null}
          </StepCard>

          <StepCard
            step={5}
            title="ສ່ວນຫຼຸດ ແລະ ຮູບແບບການເງິນ"
            subtitle="draft / quoted / locked — ເລືອກວົງຈອນລາຄາກ່ອນເກັບເງິນ"
          >
            <RHF.Controller
              name="pricingStatus"
              control={methods.control}
              render={({ field }) => (
                <fieldset className="space-y-2 border-0 p-0">
                  <legend className="font-medium text-sm">ວົງຈອນລາຄາ</legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {(
                      [
                        {
                          value: "draft" as const,
                          title: "Draft",
                          hint: "ຍັງບໍ່ກຳນົດລາຄາ",
                        },
                        {
                          value: "quoted" as const,
                          title: "Quoted",
                          hint: "ໃສ່ລາຄາ · ຍັງບໍ່ເກັບເງິນ",
                        },
                        {
                          value: "locked" as const,
                          title: "Locked",
                          hint: "ກຳນົດລາຄາ + ເກັບເງິນ",
                        },
                      ] as const
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className={cn(
                          "cursor-pointer rounded-lg border p-3 text-left",
                          field.value === opt.value
                            ? "border-primary bg-primary/5"
                            : "border-border",
                        )}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          checked={field.value === opt.value}
                          onChange={() => field.onChange(opt.value)}
                        />
                        <span className="block font-medium text-sm">
                          {opt.title}
                        </span>
                        <span className="mt-0.5 block text-muted-foreground text-xs">
                          {opt.hint}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            />

            {pricingStatus !== "draft" ? (
            <div className="space-y-3 border-t pt-2">
              <RHF.Controller
                name="discountType"
                control={methods.control}
                render={({ field }) => (
                  <SellPackageDiscountTypeRadio
                    value={
                      (field.value as SellPackageDiscountType | undefined) ??
                      "none"
                    }
                    onChange={(next) => {
                      field.onChange(next);
                      if (next === "none") {
                        methods.setValue("discountValue", undefined, {
                          shouldValidate: false,
                        });
                      }
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />

              {(discountTypeWatch === "percentage" ||
                discountTypeWatch === "fixed") && (
                  <div className="max-w-sm">
                    {discountTypeWatch === "percentage" ? (
                      <FormNumber
                        name="discountValue"
                        label="ສ່ວນຫຼຸດ (%)"
                        placeholder="ປ້ອນສ່ວນຫຼຸດ (0-100)"
                        min={0}
                        max={100}
                      />
                    ) : (
                      <FormPrice
                        name="discountValue"
                        label="ສ່ວນຫຼຸດ (ກີບ)"
                        placeholder="ປ້ອນຈຳນວນເງິນ"
                        currencySymbol="₭"
                      />
                    )}
                  </div>
                )}
            </div>
            ) : null}

            {pricingStatus === "locked" ? (
            <div className="mt-6 grid gap-2 border-t pt-4">
              <RHF.Controller
                name="settlementMode"
                control={methods.control}
                render={({ field }) => (
                  <fieldset className="min-w-0 border-0 p-0">
                    <legend className="mb-2 font-medium text-sm">
                      ຮູບແບບການເງິນ
                    </legend>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label
                        className={cn(
                          "flex cursor-pointer gap-3 rounded-lg border p-4 text-left ring-offset-background transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2",
                          field.value === "receipt"
                            ? "border-red-600 bg-muted/50"
                            : "border-border hover:bg-muted/30",
                        )}
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value="receipt"
                          checked={field.value === "receipt"}
                          onChange={() => field.onChange("receipt")}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          className="sr-only"
                        />
                        <Receipt
                          className={cn(
                            "mt-0.5 size-5 shrink-0 stroke-[1.5]",
                            field.value === "receipt"
                              ? "text-red-600"
                              : "text-muted-foreground",
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 space-y-1">
                          <span className="block font-semibold text-sm leading-tight">
                            ໃບເກັບເງິນເຕັມ
                          </span>
                          <span className="block text-muted-foreground text-xs leading-snug">
                            ຊຳລະຄົບຕາມລາຄາສຸດທ້າຍ
                          </span>
                        </span>
                      </label>
                      <label
                        className={cn(
                          "flex cursor-pointer gap-3 rounded-lg border p-4 text-left ring-offset-background transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2",
                          field.value === "credit"
                            ? "border-red-600 bg-muted/50"
                            : "border-border hover:bg-muted/30",
                        )}
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value="credit"
                          checked={field.value === "credit"}
                          onChange={() => field.onChange("credit")}
                          onBlur={field.onBlur}
                          className="sr-only"
                        />
                        <ScrollText
                          className={cn(
                            "mt-0.5 size-5 shrink-0 stroke-[1.5]",
                            field.value === "credit"
                              ? "text-red-600"
                              : "text-muted-foreground",
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 space-y-1">
                          <span className="block font-semibold text-sm leading-tight">
                            ໃບເກັບເງິນ / ຊຳລະບາງສ່ວນ
                          </span>
                          <span className="block text-muted-foreground text-xs leading-snug">
                            ຮັບເງິນບາງສ່ວນຕອນຂາຍ ຫຼື ຕິດຕາມທີ່ໜ້າບິນ
                          </span>
                        </span>
                      </label>
                    </div>
                  </fieldset>
                )}
              />
              {settlementMode === "credit" &&
                priceCalculation.finalPrice > 0 ? (
                <div className="space-y-4">
                  <FormNumber
                    name="installmentCount"
                    label="ຈຳນວນງວດຊຳລະ"
                    min={2}
                    max={12}
                    step={1}
                    requiredMark
                  />
                  {installmentRowsWatch.length > 0 ? (
                    <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="font-medium text-muted-foreground text-xs uppercase">
                          ແຜນງວດ (due ທຸກ 30 ວັນ · ແກ້ຍອດແຕ່ລະງວດໄດ້)
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={rebuildInstallmentRows}
                        >
                          ແບ່ງເທົ່າ ๆ ຄືນ
                        </Button>
                      </div>
                      <div
                        className={cn(
                          "mb-3 rounded-lg border px-3 py-2.5 tabular-nums",
                          installmentPlanBalance.isValid
                            ? "border-green-600/40 bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300"
                            : installmentPlanBalance.balance > 0
                              ? "border-amber-600/40 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                              : "border-destructive/40 bg-destructive/5 text-destructive",
                        )}
                      >
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium">ຍອດຄ້າງ</span>
                          <span className="text-lg font-semibold">
                            {installmentPlanBalance.isValid
                              ? "0 ກີບ — ຄົບແລ້ວ"
                              : `${Math.abs(installmentPlanBalance.balance).toLocaleString("lo-LA")} ກີບ${
                                  installmentPlanBalance.balance > 0
                                    ? " (ຍັງຂາດ)"
                                    : " (ເກີນ)"
                                }`}
                          </span>
                        </div>
                        <p className="mt-1 text-xs opacity-80">
                          ລວມແຜນ{" "}
                          {installmentPlanBalance.sum.toLocaleString("lo-LA")}{" "}
                          /{" "}
                          {installmentPlanBalance.total.toLocaleString("lo-LA")}{" "}
                          ກີບ
                        </p>
                      </div>
                      <div className="space-y-3">
                        {installmentRowsWatch.map((row, index) => {
                          const remainingAfterRow =
                            priceCalculation.finalPrice -
                            sumInstallmentAmounts(
                              installmentRowsWatch.slice(0, index + 1),
                            );
                          return (
                            <div
                              key={row.installmentNo}
                              className="grid gap-2 sm:grid-cols-[1fr_160px]"
                            >
                              <div className="flex flex-col justify-center">
                                <span className="font-medium">{row.label}</span>
                                <span className="text-muted-foreground text-xs">
                                  {formatDate(row.dueDate)}
                                </span>
                              </div>
                              <div className="space-y-1">
                                <FormPrice
                                  name={`installmentRows.${index}.amount`}
                                  label="ຍອດງວດ (ກີບ)"
                                  currencySymbol="₭"
                                  min={1}
                                  step={1}
                                />
                                <p
                                  className={cn(
                                    "text-right text-xs tabular-nums",
                                    remainingAfterRow === 0
                                      ? "font-medium text-green-600"
                                      : remainingAfterRow > 0
                                        ? "text-muted-foreground"
                                        : "font-medium text-destructive",
                                  )}
                                >
                                  ຍອດຄ້າງ:{" "}
                                  {remainingAfterRow.toLocaleString("lo-LA")}{" "}
                                  ກີບ
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div
                        className={cn(
                          "mt-3 space-y-1 border-t pt-2 font-medium tabular-nums",
                          installmentPlanBalance.isValid
                            ? "text-green-600"
                            : "text-destructive",
                        )}
                      >
                        <div className="flex justify-between gap-3">
                          <span>
                            {installmentPlanBalance.isValid
                              ? "ຜົນລວມຖືກຕ້ອງ"
                              : installmentPlanBalance.balance > 0
                                ? "ຍັງຂາດ"
                                : "ເກີນ"}
                          </span>
                          <span>
                            {installmentPlanBalance.sum.toLocaleString("lo-LA")}{" "}
                            /{" "}
                            {installmentPlanBalance.total.toLocaleString(
                              "lo-LA",
                            )}{" "}
                            ກີບ
                          </span>
                        </div>
                        {!installmentPlanBalance.isValid ? (
                          <p className="text-destructive text-xs font-normal leading-snug">
                            {formatInstallmentTotalMismatchMessage(
                              installmentPlanBalance.sum,
                              installmentPlanBalance.total,
                            )}
                          </p>
                        ) : null}
                      </div>
                      {typeof methods.formState.errors.installmentRows
                        ?.message === "string" ? (
                        <p className="mt-2 text-destructive text-xs">
                          {methods.formState.errors.installmentRows.message}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <FormPrice
                    name="amountPaidNow"
                    label="ຈຳນວນທີ່ຮັບຕອນຂາຍ — 0 ຖ້າຍັງບໍ່ຮັບ"
                    placeholder="0"
                    currencySymbol="₭"
                    min={0}
                    max={priceCalculation.finalPrice}
                    step={1}
                  />
                </div>
              ) : null}
              {settlementMode === "credit" &&
                priceCalculation.finalPrice <= 0 ? (
                <p className="text-muted-foreground text-xs">
                  ລາຄາສຸດທ້າຍເປັນ 0 — ບໍ່ສ້າງໃບເກັບເງິນ
                </p>
              ) : null}
            </div>
            ) : (
              <p className="mt-4 rounded-lg border border-dashed bg-muted/20 px-3 py-2 text-muted-foreground text-xs">
                {pricingStatus === "draft"
                  ? "Draft — ບັນທຶກໂຄງການແລ້ວ lock ລາຄາ/ເກັບເງິນທີ່หน้າ success"
                  : "Quoted — ບັນທຶກລາຄາແລ້ວ lock & ເກັບເງິນທີหน้า success"}
              </p>
            )}
          </StepCard>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="submit"
              disabled={submitting || installmentPlanInvalid}
            >
              {pricingStatus === "draft"
                ? "ບັນທຶກ Draft"
                : pricingStatus === "quoted"
                  ? "ບັນທຶກ Quoted"
                  : "ບັນທຶກການຂາຍ"}
            </Button>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex gap-3">
              <Building2Icon
                className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0">
                <p className="font-semibold text-base">Partner · ອອກສະຖານທີ່</p>
                <p className="text-muted-foreground text-sm leading-snug">
                  ລາຍການຕາມໂຄງການ — ບໍ່ເຊື່ອມແພັກເກດນັກຮຽນ
                </p>
              </div>
            </div>
            {pricingStatus === "draft" ? (
              <FormPrice
                name="provisionalTotal"
                label="ຍອດປະມານ (ທາງເລືອກ)"
                placeholder="0"
                currencySymbol="₭"
              />
            ) : (
              <FormPrice
                name="price"
                label="ຍອດຕາມໂຄງການ ກ່ອນຫຼຸດ (ກີບ)"
                placeholder="ປ້ອນຍອດຕາມສັນຍາ"
                currencySymbol="₭"
                requiredMark
              />
            )}
            {priceCalculation.originalPrice > 0 ? (
              <div className="mt-4 space-y-1 rounded-lg border bg-muted/50 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    ຕາມສັນຍາ (ກ່ອນຫຼຸດ):
                  </span>
                  <span className="tabular-nums">
                    {priceCalculation.originalPrice.toLocaleString("lo-LA")} ກີບ
                  </span>
                </div>
                {priceCalculation.discountAmount > 0 ? (
                  <div className="flex justify-between text-green-600">
                    <span>ສ່ວນຫຼຸດ:</span>
                    <span className="font-medium tabular-nums">
                      -{priceCalculation.discountAmount.toLocaleString("lo-LA")}{" "}
                      ກີບ
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between border-t pt-2 font-semibold">
                  <span className="text-foreground">ລາຄາສຸດທ້າຍ:</span>
                  <span className="text-red-600 tabular-nums">
                    {priceCalculation.finalPrice.toLocaleString("lo-LA")} ກີບ
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </aside>
      </div>
    </FormRoot>
  );
}
